import FilterBase from "./filterBase.js";
import * as tf from '@tensorflow/tfjs';
import TensorFlowModelInitiator from "../../../../../utils/tensorflowModelInitiator.js";

export default class AlphaMatting extends FilterBase {
    objectType = "AlphaMattingFilterItem";

    constructor(args = {}) {
        super(args);
        this.objectToClassValue(args);
    }

    objectToClassValue(args = {}) {
        if (args.tooltip == null) {
            args.tooltip = "Retire les fonds de l'image. (Ce filtre n'est pas visible dans l'interface d'édition quand il est utilisé)";
        }
        super.objectToClassValue(args);
    }

    async filterAction(cv, cvImage) {
        // Validation des entrées
        if (!cv || !cvImage || cvImage.empty()) {
            console.warn("Image d'entrée invalide pour Alpha Matting");
            return cvImage;
        }

        if (cvImage.cols === 0 || cvImage.rows === 0) {
            console.warn("Image vide pour Alpha Matting");
            return cvImage;
        }

        try {
            // Obtenir le modèle via TensorFlowModelInitiator
            const model = await TensorFlowModelInitiator.getModel();
            const planSize = this.planSize;
            let [imgWidth, imgHeight] = [cvImage.cols, cvImage.rows];
            // Redimensionnement si nécessaire
            if (planSize && (cvImage.cols !== planSize.width || cvImage.rows !== planSize.height)) {
                const proportion = cvImage.cols / cvImage.rows;
                if (proportion > 1) {
                    imgWidth = planSize.width;
                    imgHeight = Math.round(imgWidth / proportion);
                } else {
                    imgHeight = planSize.height;
                    imgWidth = Math.round(imgHeight * proportion);
                }
                
                // Redimensionner en créant une nouvelle image
                const resizedImage = new cv.Mat();
                cv.resize(cvImage, resizedImage, new cv.Size(imgWidth, imgHeight));
                cvImage.delete();
                cvImage = resizedImage;
            }
            // 2. Conversion OpenCV vers TensorFlow.js avec conversion BGRA vers RGB
            let srcTensor;
            if (cvImage.channels() === 4) {
                // Conversion BGRA vers RGB
                const bgrImage = new cv.Mat();
                cv.cvtColor(cvImage, bgrImage, cv.COLOR_BGRA2BGR);
                
                // Créer le tensor avec 3 canaux
                srcTensor = tf.tensor(bgrImage.data, [bgrImage.rows, bgrImage.cols, bgrImage.channels()]);
                
                // Nettoyer l'image BGR temporaire
                bgrImage.delete();
            } else {
                // Si déjà 3 canaux, créer directement le tensor
                srcTensor = tf.tensor(cvImage.data, [cvImage.rows, cvImage.cols, cvImage.channels()]);
            }

            // Normalisation
            const normalizedSrc = tf.tidy(() => srcTensor.toFloat().expandDims(0).div(255));
            // 3. Création des entrées RVM
            const inputs = this.createRVMInputs(normalizedSrc, imgWidth, imgHeight);
            // 4. Inférence TensorFlow.js
            const result = await this.runRVMInference(model, inputs);
            // 5. Postprocessing - Redimensionnement et application du masque alpha
            const postprocessedImage = this.postprocessAlphaMask(cv, result[0], result[1]);
            // Nettoyage
            normalizedSrc.dispose();
            srcTensor.dispose();
            this.disposeTensors(inputs);
            cvImage.delete();
            result.forEach(tensor => tensor.dispose());
            
            return postprocessedImage;
            
        } catch (error) {
            console.error("Erreur lors de l'application du filtre Alpha Matting:", error);
            return cvImage;
        }
    }

    /**
     * Postprocessing du masque alpha
    */
    async postprocessAlphaMask(cv, fgr = null, pha = null) {
        if (!cv) {
            throw new Error("Image ou masque alpha invalide");
        }

        const rgba = tf.tidy(() => {
            const rgb = (fgr !== null) ?
                fgr.squeeze(0).mul(255).cast('int32') :
                tf.fill([pha.shape[1], pha.shape[2], 3], 255, 'int32');
            const a = (pha !== null) ?
                pha.squeeze(0).mul(255).cast('int32') :
                tf.fill([fgr.shape[1], fgr.shape[2], 1], 255, 'int32');
            const modifiedRgb = tf.where(
                tf.equal(a, 0),
                tf.onesLike(rgb).mul(255),
                rgb
            );
            return tf.concat([modifiedRgb, a], -1);
        });
        const [height, width] = rgba.shape.slice(0, 2);
        const pixelData = new Uint8ClampedArray(await rgba.data());
        const imageData = new ImageData(pixelData, width, height);
        rgba.dispose();
        return cv.matFromImageData(imageData);
    }

    /**
     * Création des entrées RVM selon la spécification officielle
     */
    createRVMInputs(srcTensor, imgWidth, imgHeight) {
        // 384x384
        const downsample_ratio = Math.round(Math.min(384 / imgHeight, 384 / imgWidth) * 100) / 100;
        console.log("Downsample ratio:", downsample_ratio, imgWidth, imgHeight);
        // Entrées RVM standard selon la documentation
        const inputs = {
            'src': srcTensor,
            'r1i': tf.tensor(0.),
            'r2i': tf.tensor(0.),
            'r3i': tf.tensor(0.),
            'r4i': tf.tensor(0.),
            'downsample_ratio': tf.tensor(downsample_ratio > 1 ? 1 : downsample_ratio)
        };
        return inputs;
    }

    /**
     * Exécution de l'inférence RVM
     */
    async runRVMInference(model, inputs) {
        try {
            // Utiliser executeAsync pour les opérations dynamiques
            const result = await model.executeAsync(inputs, ['fgr', 'pha', 'r1o', 'r2o', 'r3o', 'r4o']);
            return result;
            
        } catch (error) {
            console.error("Erreur lors de l'inférence RVM:", error);
            throw error;
        }
    }

    /**
     * Nettoyage des tenseurs
     */
    disposeTensors(inputs) {
        Object.values(inputs).forEach(tensor => {
            if (tensor && tensor.dispose) {
                tensor.dispose();
            }
        });
    }

    dispose() {
        // Le modèle est géré par TensorFlowModelInitiator (singleton)
        this.modelInitiator = null;
    }

    static get Name() {
        return "Alpha Matting";
    }
}